import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { MAJORS, PARENTF } from "../mock/data";
import I18N from "../mock/i18n";
import { BrandmarkSVG } from "./Compass";
import { BarChart3, Users, GitFork, Sliders, LogOut, KeyRound } from "lucide-react";

function Bar({ label, value, max, tone = "brass" }) {
  const pct = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  const fill = tone === "coral" ? "var(--coral)" : "var(--brass)";
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
        <span style={{ color: "var(--parchment)" }}>{label}</span>
        <span style={{ fontFamily: "var(--font-mono)", color: "var(--brass-bright)" }}>{value}</span>
      </div>
      <div style={{ height: 8, background: "var(--navy-deep)", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: pct + "%", height: "100%", background: fill, transition: "width .3s ease" }} />
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div style={{ background: "var(--navy)", border: "1px solid var(--line)", borderRadius: 14, padding: 18, flex: 1, minWidth: 160 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--brass-bright)", opacity: .85, marginBottom: 8 }}>
        {Icon && <Icon size={14} />}<span>{label}</span>
      </div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 30, color: "var(--parchment)", fontWeight: 600 }}>{value}</div>
    </div>
  );
}

export default function Counselors() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [gateError, setGateError] = useState(null);
  const [gatePassed, setGatePassed] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async (accessCode) => {
    setLoading(true);
    try {
      const res = await api.counselorAnalytics(accessCode || null);
      setData(res);
      setGatePassed(true);
    } catch (e) {
      if (e.status === 403) {
        setGatePassed(false);
      } else {
        setGateError(e.data?.detail || "Failed to load");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // try silently first — pro users get in via cookie
    load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCode = async (e) => {
    e.preventDefault();
    setGateError(null);
    try {
      await api.counselorVerify(code);
      // if verify passes, load analytics with the code
      await load(code);
    } catch (err) {
      setGateError(err.data?.detail || "Invalid code");
    }
  };

  const t = I18N.en;

  if (!gatePassed && !loading) {
    return (
      <div className="App">
        <div className="topbar">
          <Link to="/" className="brandmark" style={{textDecoration:'none'}}><BrandmarkSVG /><span>{t.brand}</span></Link>
          {user && <button className="btn-secondary" onClick={logout} style={{padding:"8px 14px",fontSize:13}}><LogOut size={14} style={{verticalAlign:'middle',marginRight:6}}/>Sign out</button>}
        </div>
        <div className="wrap">
          <div className="card" style={{maxWidth:520,margin:"40px auto 26px"}}>
            <div className="hintline" style={{margin:0,marginBottom:8}}><KeyRound size={13} style={{verticalAlign:'middle',marginRight:6}}/>COUNSELOR ACCESS</div>
            <h2>Enter your counselor code</h2>
            <p style={{opacity:.85}}>Counselors and educators can view aggregate analytics with a shared access code, or unlock full access with a Pro account.</p>
            <form onSubmit={submitCode}>
              <div className="field">
                <label>Access code</label>
                <input type="text" value={code} onChange={(e)=>setCode(e.target.value)} placeholder="e.g., counselor2025" />
              </div>
              {gateError && <p style={{color:"var(--coral)", fontSize:13}}>{gateError}</p>}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:10}}>
                <Link to="/dashboard" style={{color:"var(--brass-bright)",fontSize:13,textDecoration:"none"}}>Have a Pro account? Sign in →</Link>
                <button className="btn-primary" type="submit">Continue</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !data) return <div className="wrap" style={{padding:80,color:"var(--white-soft)"}}>Loading analytics…</div>;

  const maxMajor = Math.max(...(data.top_major_counts || []).map((d) => d.count), 1);
  const maxParent = Math.max(...(data.parent_field_counts || []).map((d) => d.count), 1);
  const maxLang = Math.max(...(data.lang_distribution || []).map((d) => d.count), 1);
  const maxDay = Math.max(...(data.runs_by_day || []).map((d) => d.count), 1);

  return (
    <div className="App">
      <div className="topbar">
        <Link to="/" className="brandmark" style={{textDecoration:'none'}}><BrandmarkSVG /><span>{t.brand}</span></Link>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {user && <span style={{fontSize:13,opacity:.8}}>{user.name}</span>}
          {user && <button className="btn-secondary" onClick={logout} style={{padding:"8px 14px",fontSize:13}}><LogOut size={14} style={{verticalAlign:'middle',marginRight:6}}/>Sign out</button>}
        </div>
      </div>

      <div className="wrap">
        <div className="card">
          <div className="hintline" style={{margin:0,marginBottom:8}}>COUNSELOR ANALYTICS</div>
          <h2>Aggregate bearings</h2>
          <div className="sub">Anonymized totals across every bearing taken. Use this to spot pressure patterns and language reach.</div>

          <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:8}}>
            <StatCard label="Total runs" value={data.total_runs} icon={Users} />
            <StatCard label="Avg family weight" value={data.avg_family_weight + " / 100"} icon={Sliders} />
            <StatCard label="Compass conflict rate" value={data.conflict_rate + "%"} icon={GitFork} />
          </div>
        </div>

        <div className="card">
          <h2 style={{display:"flex",alignItems:"center",gap:8,fontSize:22}}><BarChart3 size={20} color="var(--brass-bright)"/>Top #1 majors</h2>
          <div className="sub">Which major finished first, most often.</div>
          {(data.top_major_counts || []).length === 0 && <p style={{opacity:.7}}>No data yet.</p>}
          {(data.top_major_counts || []).map((row) => {
            const idx = MAJORS.findIndex((m) => m.s === row._id);
            const label = idx >= 0 ? t.major[idx] : row._id;
            return <Bar key={row._id} label={label} value={row.count} max={maxMajor} />;
          })}
        </div>

        <div className="card">
          <h2 style={{fontSize:22}}>Family-hope patterns</h2>
          <div className="sub">How often each field is voiced by parents.</div>
          {(data.parent_field_counts || []).length === 0 && <p style={{opacity:.7}}>No data yet.</p>}
          {(data.parent_field_counts || []).map((row) => {
            const idx = PARENTF.indexOf(row.slug);
            const label = idx >= 0 ? t.parent[idx] : row.slug;
            return <Bar key={row.slug} label={label} value={row.count} max={maxParent} tone="coral" />;
          })}
        </div>

        <div className="card">
          <h2 style={{fontSize:22}}>Language mix</h2>
          {(data.lang_distribution || []).map((row) => (
            <Bar key={row._id} label={row._id?.toUpperCase() || "?"} value={row.count} max={maxLang} />
          ))}
        </div>

        <div className="card">
          <h2 style={{fontSize:22}}>Runs in last 14 days</h2>
          <div style={{display:"flex",alignItems:"flex-end",gap:6,height:120,padding:"10px 0"}}>
            {(data.runs_by_day || []).length === 0 && <span style={{opacity:.7}}>No runs yet.</span>}
            {(data.runs_by_day || []).map((row) => {
              const h = Math.max(6, Math.round((row.count / maxDay) * 100));
              return (
                <div key={row._id} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
                  <div style={{width:"100%",height:h+"%",background:"var(--brass)",borderRadius:4,minHeight:6}} title={`${row._id}: ${row.count}`}/>
                  <div style={{fontSize:10,fontFamily:"var(--font-mono)",opacity:.6}}>{row._id.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
